import { ArticleType, BodyType, Category, Gear } from '@nemuru-eng/js-api-client';
import { z } from 'zod';

import { getIsIdentificationNumberTaken } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/schemas/identificationNumberTakenState';
import { MinimalFormItemSchema } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/schemas/MinimalFormItemSchema';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';
import { requiredEnum, requiredNumber, requiredPastDate, requiredString } from '@/utils/zodUtils';
import { isValidSpanishLicensePlate, normalizeSpanishLicensePlate } from '@/utils/zodValidation/validateSpanishLicensePlate';

export const AutomobileFormItemSchema = (t: GetTextIdTranslationType) =>
  MinimalFormItemSchema(t)
    .merge(
      z
        .object({
          type: z.literal(ArticleType.AUTOMOBILE),
          description: z.string().optional(),
          brand: requiredString(t),
          model: requiredString(t),
          version: requiredString(t),
          identificationNumber: z
            .string()
            .regex(/^[a-zA-Z0-9\\/\-_+@\s]*$/, t('REFERENCE_CHARACTER_NOT_ALLOWED'))
            .optional()
            .refine(
              (data) => {
                if (!data) return true;

                if (data && data.length === 17) return true;

                return false;
              },
              { message: t('IDENTIFICATION_NUMBER_MUST_BE_17_CHARACTERS') },
            )
            .refine(
              (data) => {
                if (!data) return true;

                return !getIsIdentificationNumberTaken();
              },
              { message: t('IDENTIFICATION_NUMBER_ALREADY_USED_HINT') },
            ),
          isNewVehicle: z.boolean(),
          registrationDate: requiredPastDate(t).nullable().optional(),
          plateNumber: z
            .string()
            .optional()
            .transform((data) => (data ? normalizeSpanishLicensePlate(data) : data))
            .refine(
              (data) => {
                if (!data) {
                  return true;
                }

                return isValidSpanishLicensePlate(data);
              },
              { message: t('INVALID_SPANISH_LICENSE_PLATE') },
            ),
          odometer: z
            .string()
            .nullable()
            .optional()
            .refine(
              (data) => {
                if (!data) return true;
                return data && parseInt(data) >= 0;
              },
              {
                message: t('VALUE_MUST_BE_POSITIVE'),
              },
            ),
        })
        .strict(),
    )
    .merge(AutomobileAdditionalDataFromCdnSchema(t));

const AutomobileAdditionalDataFromCdnSchema = (t: GetTextIdTranslationType) =>
  z
    .object({
      brandCode: z.string().nullable(),
      modelCode: z.string().nullable(),
      versionCode: z.string().nullable(),
      autoCategory: requiredEnum(Category, t).nullable(),
      bodyType: requiredEnum(BodyType, t).nullable(),
      power: requiredNumber(t, 0).nullable(),
      engine: requiredNumber(t, 0).nullable(),
      gear: requiredEnum(Gear, t).nullable(),
    })
    .strict();
